// Types for the new chat UI. The catalog of models / modes / aspect-ratios /
// resolutions / durations is fetched at runtime from
// `GET /api/v2/generation/settings` (see `entities/model/api.ts`).
// Human-readable labels come from the backend too (`ModelConfig.name`,
// `ModelConfig.brandLabel`, `ModeConfig.title`) — the frontend renders them
// as-is and keeps no label maps of its own: a new model / mode /
// brand added on the backend shows up here without a frontend change.

export type ArtifactType = "image" | "video";

export type GenerationMode =
    | "text_to_video"
    | "text_to_image"
    | "frames"
    | "references"
    | "image_to_video"
    | "edit"
    // Апскейл — два РАЗНЫХ режима каталога, а не один с двумя артефактами:
    // карточка Topaz объявляет оба, у каждого свой канал входа и свои оси.
    | "upscale_image"
    | "upscale_video"
    | "motion_control"
    | "omni_reference";

export type ModelBrand = "google" | "kling" | "grok" | "bytedance" | "topaz" | "openai" | "minimax";

export type AudioSupport = "always" | "optional" | "none";

// Роль входной картинки режима. Режимы Kling с фичей
// `first_last_frame` принимают кадры не пулом, а по ролям — угадывать роль по
// порядку нельзя (omni на этом падал `Playground task failed`), поэтому UI
// спрашивает каждый кадр отдельным слотом.
export const IMAGE_INPUT_ROLES = ["first_frame", "last_frame"] as const;

export type ImageInputRole = (typeof IMAGE_INPUT_ROLES)[number];

export function isImageInputRole(value: string): value is ImageInputRole {
    return (IMAGE_INPUT_ROLES as readonly string[]).includes(value);
}

// Каналы входа так, как их различает домен: единица, у которой есть СВОИ
// требования к исполнителю. Это не то же, что колонки бара
// (`GenerationInputChannel` в entities/chat) — там визуальные группы: канал
// кадров рисуется одной колонкой, а требует чего-то только пара кадров.
// Одиночного первого кадра здесь нет намеренно: он не требует ничего.
export const GENERATION_CHANNELS = [
    "frame_pair",
    "reference_images",
    "elements",
    "input_video",
    "sound",
] as const;

export type GenerationChannel = (typeof GENERATION_CHANNELS)[number];

export interface ChannelRequirement {
    readonly channel: GenerationChannel;
    readonly features: readonly string[];
}

export interface ModeConfig {
    readonly name: GenerationMode;
    readonly title: string;
    readonly description: string;
    readonly aspectRatios: readonly string[];
    // Long-side pixel count (e.g. 1024 / 2048 / 4096). Format with
    // `resolutionLabel` only when rendering; never store the label in state.
    readonly resolutions: readonly number[];
    readonly durations: readonly number[];
    readonly upscaleFactors: readonly number[];
    readonly grokImagineModes: readonly string[];
    readonly audioSupport: AudioSupport;
    readonly defaultAspectRatio: string | null;
    readonly defaultResolution: number | null;
    readonly defaultDuration: number | null;
    readonly defaultUpscaleFactor: number | null;
    readonly defaultGrokImagineMode: string | null;
    // Kling Motion Control: обязательная нативная настройка character_orientation
    // ("video" / "image"); пусто — у режима её нет.
    readonly characterOrientations: readonly string[];
    readonly defaultCharacterOrientation: string | null;
    // Союз фич эталона, покрытых хотя бы одним активным провайдером режима
    // (KlingFeatureEnum values). Отвечает на «что режим предлагает вообще», но
    // НЕ на «исполним ли этот набор входов»: запрос исполняет один провайдер, а
    // союз склеивает возможности разных. Исполнимость — по двум полям ниже.
    readonly features: readonly string[];
    // Наборы фич отдельных строк каталога активных провайдеров. Набор входов
    // исполним, когда требования его каналов укладываются в ОДИН такой набор
    // (`entities/model/lib/channel-availability`).
    readonly providerFeatureSets: readonly (readonly string[])[];
    // Что каждый канал входа требует от исполнителя. Приходит с бэка, чтобы у
    // фронта не завелась вторая копия доменного правила: разъехавшись с
    // маршрутизацией, она гасила бы не то и пропускала бы неисполнимое.
    readonly channelRequirements: readonly ChannelRequirement[];
    // Именованные слоты картинок в порядке `inputImageUrls`:
    // `imageRoles[i]` — роль `inputImageUrls[i]`. Пусто — ролей у режима нет,
    // работает плоский пул вложений (поведение остальных моделей).
    readonly imageRoles: readonly ImageInputRole[];
    // Сколько элементов библиотеки принимает режим; null/0 — пикер элементов
    // не показываем (фича не покрыта провайдером или снята платформенным гейтом).
    readonly maxElements: number | null;
    // Референсные картинки — отдельный канал режима (нативный Omni: `refer_image`),
    // не кадр и не элемент. null/0 — канала у режима нет. В отличие от `maxElements`
    // приходит из эталона модели и НЕ гейтится покрытием: исполнимость отвечает
    // `referenceImagesSupported`.
    readonly maxReferenceImages: number | null;
    // Исполняет ли канал хоть один подключённый провайдер. false при непустом
    // `maxReferenceImages` = модель умеет, наш маршрут — нет: слоты показываем
    // неактивными и объясняем причину, а не принимаем картинку в никуда.
    readonly referenceImagesSupported: boolean;
    readonly maxInputImages: number | null;
    readonly maxInputVideos: number | null;
    readonly maxInputAudio: number | null;
    // Минимум входных изображений: 0 — режим не требует, >0 — submit
    // нужно блокировать, пока пользователь не загрузит N картинок.
    readonly minInputImages: number;
    // Минимум визуальных входов режима — картинок ЛЮБОГО канала или видео.
    // У мультимодального режима кадрового канала нет вовсе (`maxInputImages: 0`),
    // и `minInputImages` его требование выразить не может: обязательность там
    // закрывается референсом или видео, а одного аудио модели мало.
    readonly minVisualInputs: number;
    readonly maxPromptLength: number | null;
    readonly supportsNegativePrompt: boolean;
    // Сколько результатов режим отдаёт за одну генерацию — потолок самого
    // способного из активных провайдеров (нативный пакет или эмуляция). 1 —
    // пакета не умеет никто: плитки больше единицы блокируем с причиной, иначе
    // человек вместо объяснения увидит ошибку цены (бэк отказывает считать
    // цену пакета, который никто не исполнит).
    readonly maxBatchSize: number;
}

export interface ModelConfig {
    readonly id: string;
    readonly name: string;
    readonly brand: ModelBrand;
    // Человекочитаемый лейбл бренда с бэка (например "Kling AI") — рендерится как есть.
    readonly brandLabel: string;
    readonly artifactType: ArtifactType;
    readonly modes: readonly ModeConfig[];
}

export const ARTIFACT_TYPES: readonly { id: ArtifactType; label: string }[] = [
    { id: "image", label: "Image" },
    { id: "video", label: "Video" },
];

export function findModeConfig(
    model: ModelConfig | null | undefined,
    mode: GenerationMode,
): ModeConfig | null {
    if (!model) return null;
    return model.modes.find((m) => m.name === mode) ?? null;
}

export function getModelById(
    models: readonly ModelConfig[] | null | undefined,
    id: string | null | undefined,
): ModelConfig | undefined {
    if (!id || !models) return undefined;
    return models.find((m) => m.id === id);
}
